import sectionBg from '@/styles/sectionBackgrounds.module.scss';
import PageHead from '@/components/PageHead';
import {cdnUrl} from '@/util/cdnImage';
import {IOS_META_DATA} from '@/util/metaData/mobile';
import {stripHtmlForJsonLd} from '@/util/seo-utils';

// Sections are imported statically on purpose: next/dynamic gives every section its own render-blocking CSS file;
// static imports ship one page stylesheet. The JS is MUI-free and small.
import HeroVideoBanner from '@/components/plain-sections/HeroVideoBanner';
import ClientServed from '@/components/home-plain/ClientServed';
import MvpStatsSection from '@/components/plain-sections/MvpStatsSection';
import MvpEndToEndServices from '@/components/plain-sections/MvpEndToEndServices';
import IosChallenges from '@/components/plain-sections/IosChallenges';
import IosSuccessStories from '@/components/plain-sections/IosSuccessStories';
import MvpTestimonials from '@/components/plain-sections/MvpTestimonials';
import IosFeatureCards from '@/components/plain-sections/IosFeatureCards';
import MvpTypes from '@/components/plain-sections/MvpTypes';
import HomeIndustryTabs from '@/components/home-plain/HomeIndustryTabs';
import AiStackTable from '@/components/plain-sections/AiStackTable';
import IosProcessIndex from '@/components/plain-sections/IosProcessIndex';
import ImageAccordion from '@/components/plain-sections/ImageAccordion';
import CertificationsSection from '@/components/home-plain/CertificationsSection';
import MvpCostTimeline from '@/components/plain-sections/MvpCostTimeline';
import HomeImageCTA from '@/components/home-plain/HomeImageCTA';
import HomeFAQSplit from '@/components/home-plain/HomeFAQSplit';
import LatestInsights from '@/components/plain-sections/LatestInsights';
import styles from '@/styles/iosAppDevelopment.module.scss';
import {getLatestInsights} from '@/Services/latestInsights';
import CvSection from '@/components/plain/CvSection';

const heroImage = cdnUrl('ios-app-development/ios-app-development-banner.webp');
// Intrinsic size of the hero artwork (1916 × 821).
const IOS_HERO_ASPECT_RATIO = 1916 / 821;

const IosAppDevelopmentServices: any = (props: any) => {
    const {
        openForm,
        clients,
        testimonials,
        successStories,
        iosTrustStats,
        iosServicesImage,
        iosServicesImageCaption,
        iosServices,
        iosChallengesImage,
        iosChallengePoints,
        iosChallengeImageStats,
        iosWhyNativeImage,
        iosWhyNativeCards,
        iosTechnologiesEyebrow,
        iosAdvancedTechnologies,
        iosIndustries,
        iosTechStackRows,
        iosProcessSteps,
        iosWhyChooseData,
        iosCostHighlight,
        iosCostEstimator,
        iosFinalCtaImage,
        iosFaqData,
        latestInsightsData
    } = props;

    return (
        <div>
            <PageHead meta={IOS_META_DATA} url="/ios-app-development-services" />

            <HeroVideoBanner
                heading="iOS App Development Company"
                subheading="Strong iOS products depend on the right technical foundation from the start. Our iOS App Development Services help businesses address complex integrations, performance requirements, evolving user needs and long-term scalability through architecture-led engineering. Debut Infotech combines iOS expertise with product engineering to deliver secure, high-performing applications built for sustained growth."
                primaryButtonText="Discuss Your iOS App Idea"
                onPrimaryClick={openForm}
                backgroundImage={heroImage}
                hideBackgroundImageOnMobile
                layout="split"
                splitBadgesPlacement="below-content"
                splitImageWash={false}
                splitImageAspectRatio={IOS_HERO_ASPECT_RATIO}
            />
            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <ClientServed heading="" contentData={clients} flush />
            </CvSection>
            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-deep-blue">
                <MvpStatsSection heading="Scale and delivery evidence at a glance." stats={iosTrustStats} />
            </CvSection>

            <div className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <MvpEndToEndServices
                    variant="white"
                    heading="Our iOS App Development Services"
                    subheading="As an experienced iOS App Development Company, Debut Infotech provides end-to-end engineering services for businesses building new Apple applications or improving existing products."
                    imageSrc={iosServicesImage}
                    imageCaptionTitle={iosServicesImageCaption}
                    services={iosServices}
                />
            </div>

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <IosChallenges
                    heading="Business Challenges We Solve With iOS App Development"
                    subheading="Building a successful iOS product requires the right decisions from planning through launch. Our iOS App Development Solutions help businesses clarify product direction, manage technical complexity, and build with long-term scalability in mind."
                    imageSrc={iosChallengesImage}
                    imageAlt="Product team reviewing iOS app screens, cloud, database, and analytics integrations for a new iPhone app"
                    imageStats={iosChallengeImageStats}
                    pointsTitle="We Help You Move Forward With Clarity"
                    points={iosChallengePoints}
                    ctaText="Define Your iOS App Scope"
                    onCtaClick={openForm}
                />
            </CvSection>

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-deep-blue">
                <IosSuccessStories heading="iOS App Development Case Studies" stories={successStories} onEnquire={openForm} />
            </CvSection>

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpTestimonials heading="Client Testimonials" testimonials={testimonials} />
            </CvSection>

            <div className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <IosFeatureCards
                    heading="Why Businesses Choose Native iOS Development"
                    subheading="Businesses choose Native iOS App Development for better performance, stronger security, and direct access to Apple technologies."
                    cards={iosWhyNativeCards}
                    imageSrc={iosWhyNativeImage}
                    imageAlt="Native iOS app running on an iPhone within the Apple ecosystem"
                />
            </div>

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpTypes
                    heading="Advanced Technologies We Integrate Into iOS Applications"
                    subheading="We integrate advanced technologies into iOS applications to improve user experience, support smarter workflows, and build more scalable digital products."
                    eyebrow={iosTechnologiesEyebrow}
                    types={iosAdvancedTechnologies}
                    calloutLabel="What We Integrate"
                />
            </CvSection>

            <HomeIndustryTabs
                heading="Industries We Build iOS Applications For"
                subheading="We develop iOS applications around industry-specific workflows, user expectations, integrations, and operational requirements, helping businesses turn mobile technology into practical customer and enterprise solutions."
                data={iosIndustries}
                variant="overlay"
                headerAlign="center"
            />

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding bg-deep-blue">
                <AiStackTable
                    dark
                    heading="Our Technology Stack for iOS App Development"
                    subheading="We select Apple-native frameworks, backend services, and tooling based on your product goals, performance needs, security requirements, and long-term maintainability."
                    contentData={iosTechStackRows}
                />
            </CvSection>

            <div className={`top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <IosProcessIndex
                    heading="Our iOS App Development Process"
                    subheading='Our <a href="/blog/step-by-step-ios-app-development-guide" target="_blank">iOS development process</a> moves your product from business requirements to production through defined stages for scope, experience design, architecture, engineering, quality assurance, App Store launch, and continued product evolution.'
                    steps={iosProcessSteps}
                />
            </div>

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <ImageAccordion
                    heading="Why Choose Debut Infotech as Your iOS App Development Company?"
                    subheading='Choosing the right iOS App Development Company means evaluating more than coding capability. As an iOS Application Development Company, <a href="/" target="_blank">Debut Infotech</a> combines native iOS engineering with product, cloud, data, AI, and enterprise integration expertise to help businesses build secure, scalable applications for long-term growth.'
                    items={iosWhyChooseData}
                />
            </CvSection>

            <CertificationsSection />

            <CvSection className="top-space bottom-spacing mobile-top-padding mobile-bottom-padding">
                <MvpCostTimeline
                    heading="iOS App Development Cost & Timeline"
                    subheading={
                        <>
                            The{' '}
                            <a
                                href="https://www.debutinfotech.com/blog/ios-app-development-cost-everything-you-need-to-know"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.contentLink}
                            >
                                iOS app development cost
                            </a>{' '}
                            varies significantly based on the complexity and scale of the product. According to GoodFirms&apos; 2026 iOS
                            development research, custom iOS applications can range from approximately $25,000 for simpler products to
                            $350,000+ for complex enterprise applications.
                        </>
                    }
                    highlight={iosCostHighlight}
                    estimator={iosCostEstimator}
                />
            </CvSection>

            <div>
                <HomeImageCTA
                    heading="Start Your iOS Development Project"
                    subheading="Whether you need an iOS Mobile App Development Company for a new product, Android-to-iOS migration, or enterprise application, our team can help define the architecture, engineering approach, and delivery roadmap."
                    buttonText="Discuss Your iOS Project"
                    imageSrc={iosFinalCtaImage}
                    imageAlt="iPhone showing iOS apps beside Apple and App Store icons"
                    light
                    openForm={openForm}
                />
            </div>

            <CvSection className="top-space mobile-top-padding bottom-spacing mobile-bottom-padding">
                <HomeFAQSplit
                    heading="iOS Development FAQs"
                    subheading="Answers to the questions businesses ask most often when planning, budgeting, and staffing an iOS app build."
                    faqData={iosFaqData}
                    capCardOnTablet
                />
            </CvSection>
            <div className={`latest-insights top-space bottom-spacing mobile-top-padding mobile-bottom-padding ${sectionBg.lightGradient}`}>
                <LatestInsights id="18" posts={latestInsightsData} />
            </div>

            {/* Generated from the same array the section renders, so the schema always
                matches the visible answers. */}
            <script
                type="application/ld+json"
                id="ios-app-development-faq"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'FAQPage',
                        mainEntity: iosFaqData
                            .flatMap((category: {questions: {q: string; a: string}[]}) => category.questions)
                            .map((item: {q: string; a: string}) => ({
                                '@type': 'Question',
                                name: item.q,
                                acceptedAnswer: {'@type': 'Answer', text: stripHtmlForJsonLd(item.a)}
                            }))
                    })
                }}
            />
        </div>
    );
};

// Every data list is read at build time, so none of the data modules ship in this page's client bundle.
export async function getStaticProps() {
    // Same as the homepage: the three insight cards come with the HTML instead of a client-side fetch.
    const latestInsightsData = await getLatestInsights('18');
    const [{clients}, {mvpTestimonials}, {getIosSuccessStories}, iosData] = await Promise.all([
        import('@/util/contents/home-data'),
        import('@/util/contents/mvp-development-data'),
        import('@/util/contents/ios-success-stories'),
        import('@/util/contents/ios-development-data')
    ]);
    return {
        props: {
            latestInsightsData,
            clients,
            testimonials: mvpTestimonials,
            successStories: getIosSuccessStories(),
            iosTrustStats: iosData.iosTrustStats,
            iosServicesImage: iosData.iosServicesImage,
            iosServicesImageCaption: iosData.iosServicesImageCaption,
            iosServices: iosData.iosServices,
            iosChallengesImage: iosData.iosChallengesImage,
            iosChallengePoints: iosData.iosChallengePoints,
            iosChallengeImageStats: iosData.iosChallengeImageStats,
            iosWhyNativeImage: iosData.iosWhyNativeImage,
            iosWhyNativeCards: iosData.iosWhyNativeCards,
            iosTechnologiesEyebrow: iosData.iosTechnologiesEyebrow,
            iosAdvancedTechnologies: iosData.iosAdvancedTechnologies,
            iosIndustries: iosData.iosIndustries,
            iosTechStackRows: iosData.iosTechStackRows,
            iosProcessSteps: iosData.iosProcessSteps,
            iosWhyChooseData: iosData.iosWhyChooseData,
            iosCostHighlight: iosData.iosCostHighlight,
            iosCostEstimator: iosData.iosCostEstimator,
            iosFinalCtaImage: iosData.iosFinalCtaImage,
            iosFaqData: iosData.iosFaqData
        },
        revalidate: 3600
    };
}

export default IosAppDevelopmentServices;
IosAppDevelopmentServices.Layout = 'Main';
